import type { FC, ReactNode } from 'react';

import type { Props as ButtonProps } from 'atoms/buttons/Button';
import { Button } from 'atoms/buttons/Button';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { useToggle } from 'hooks/useToggle';

import { ConfirmDialog } from '../ConfirmDialog';

type ConfirmationProps = {
  children: ReactNode;
  onConfirm: () => void;
  message?: ReactNode;
  fullScreen?: boolean;
  hideCancel?: boolean;
  disabled?: boolean;
  cta?: string;
  confirmCtaProps?: Omit<ButtonProps, 'size'> & { size?: never };
  disclaimer?: ReactNode;
};
type ButtonConfirmationProps = ConfirmationProps &
  Omit<ButtonProps, 'onClick' | 'children'> & {
    component?: FC<any>;
  };

export const ButtonBaseWithConfirmDialog = ({
  children,
  message,
  fullScreen,
  hideCancel,
  onConfirm,
  disabled,
  cta,
  confirmCtaProps,
  disclaimer,
}: ConfirmationProps) => {
  const [open, toggleOpen] = useToggle(false);

  return (
    <>
      <ButtonBase onClick={toggleOpen} disabled={disabled}>
        {children}
      </ButtonBase>
      <ConfirmDialog
        open={open}
        onClose={toggleOpen}
        cta={cta}
        message={message}
        onConfirm={onConfirm}
        fullScreen={fullScreen}
        hideCancel={hideCancel}
        ctaProps={confirmCtaProps}
        disclaimer={disclaimer}
      />
    </>
  );
};

export const ButtonWithConfirmDialog = (props: ButtonConfirmationProps) => {
  const {
    ref,
    children,
    message,
    fullScreen,
    hideCancel,
    onConfirm,
    disabled,
    cta,
    component: Component = Button,
    confirmCtaProps,
    disclaimer,
    ...rest
  } = props;

  const [open, toggleOpen] = useToggle(false);

  return (
    <>
      <Component onClick={toggleOpen} disabled={disabled} {...rest} ref={ref}>
        {children}
      </Component>
      <ConfirmDialog
        open={open}
        onClose={toggleOpen}
        cta={cta}
        message={message}
        onConfirm={onConfirm}
        fullScreen={fullScreen}
        hideCancel={hideCancel}
        ctaProps={confirmCtaProps}
        disclaimer={disclaimer}
      />
    </>
  );
};
